import { useGet, usePost, usePut } from "@/apiHooks/HttpFunctions";
import { forwardRef, useCallback, useImperativeHandle, useMemo, useState } from "react";
import { Col, Row } from "react-bootstrap";

import { currenciesApi, platformFeeApi } from "@/apis/apis";
import BasicInput from "@/components/BasicInput";
import DropDown from "@/components/DropDown/DropDown";
import RenderToast from "@/components/RenderToast";
import { LIST_MODE } from "@/constant/Lists/listMode";
import { feeAmountTypeFormOptions } from "@/constant/Payment/fee-amount-type";
import { feeCurrencyFormOptions } from "@/constant/Payment/fee-currency";
import { feeEntityFormOptions } from "@/constant/Payment/fee-entity";
import useCloseModalOnNotFound from "@/CustomHooks/useCloseModalOnNotFound";
import useFormApiState from "@/CustomHooks/useFormApiState";
import useFormikErrorToast from "@/CustomHooks/useFormikErrorToast";
import useHttpErrorToast from "@/CustomHooks/useHttpErrorToast";
import { getPlatformFeeInitialValues } from "@/data/FinanceManagement/PlatformFees/initial-values";
import BasicModal from "@/Modals/BasicModal";
import getPlatformFeeSchema from "@/schema/FinanceManagement/PlatformFees/schema";
import { formatDropdownItem } from "@/utils/helper";
import { useFormik } from "formik";

const PlatformFeeModal = forwardRef((_, ref) => {
  const [modalState, setModalState] = useState({ isOpen: false, id: null });
  const isEdit = !!modalState.id;

  const showApi = isEdit ? `${platformFeeApi}/${modalState.id}` : null;
  const { data: showData, error: showError } = useGet({
    apiURL: showApi,
    enabled: isEdit,
  });

  const handleSuccess = (action) => (data) => {
    closeModal();
    RenderToast({
      type: "success",
      message: `Platform Fee ${action} successfully.`,
    });
  };

  const {
    mutate: saveEdit,
    isPending: saveEditIsPending,
    error: saveEditError,
  } = usePut({
    apiURL: `${platformFeeApi}/${modalState.id}`,
    onSuccess: handleSuccess("edited"),
  });
  const {
    mutate: saveCreate,
    isPending: saveCreateIsPending,
    error: saveCreateError,
  } = usePost({
    apiURL: platformFeeApi,
    onSuccess: handleSuccess("added"),
  });
  const { saveIsPending, saveError, resetError } = useFormApiState(
    [saveEditIsPending, saveCreateIsPending],
    [saveEditError, saveCreateError]
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: getPlatformFeeInitialValues(isEdit ? showData : null),
    validationSchema: getPlatformFeeSchema(),
    onSubmit: (values) => handleSubmit(values),
  });

  const valueFormatter = useCallback((item) => item.code, []);
  const baseQueryParams = `?mode=${LIST_MODE.ALL}`;
  const { data: currenciesData } = useGet({
    apiURL: `${currenciesApi}${baseQueryParams}`,
    enabled: modalState.isOpen,
  });
  const validCurrencies = useMemo(() => {
    return [...feeCurrencyFormOptions, ...(currenciesData?.data?.map(item => formatDropdownItem(item, null, valueFormatter)) || [])];
  }, [currenciesData?.data, valueFormatter]);

  const handleSubmit = (values) => {
    isEdit ? saveEdit(values) : saveCreate(values);
  };

  useImperativeHandle(ref, () => ({
    open: (props) => setModalState({ isOpen: true, id: props?.id }),
    close: closeModal,
  }));

  const closeModal = useCallback(() => {
    formik.resetForm();
    resetError();
    setModalState({ isOpen: false, id: null });
  }, [resetError, formik]);

  useCloseModalOnNotFound(showError, closeModal);
  useFormikErrorToast(formik);
  useHttpErrorToast(saveError);

  return (
    <BasicModal
      header={isEdit ? "Edit Platform Fee" : "Add Platform Fee"}
      onHide={closeModal}
      show={modalState.isOpen}
      saveLoading={saveIsPending}
      formik={formik}
    >
      <Row>
        <Col md={12}>
          <DropDown
            mainClass="flex flex-row"
            dropDownContainer="w-[450px] z-[10000]"
            labelClassName="flex-grow"
            label="Fee Party"
            value={feeEntityFormOptions.find(
              (item) => item.value === formik.values.entity_type
            )}
            setValue={(selected) =>
              formik.setFieldValue("entity_type", selected.value)
            }
            options={feeEntityFormOptions}
            errorText={
              (formik.touched.entity_type && formik.errors.entity_type) ||
              (saveError?.status === 422 &&
                saveError?.response?.data?.errors?.entity_type?.join(" "))
            }
          />
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <DropDown
            mainClass="flex flex-row"
            dropDownContainer="w-[450px] z-[9999]"
            labelClassName="flex-grow"
            label="Fee Type"
            value={feeAmountTypeFormOptions.find(
              (item) => item.value === formik.values.amount_type
            )}
            setValue={(selected) =>
              formik.setFieldValue("amount_type", selected.value)
            }
            options={feeAmountTypeFormOptions}
            errorText={
              (formik.touched.amount_type && formik.errors.amount_type) ||
              (saveError?.status === 422 &&
                saveError?.response?.data?.errors?.amount_type?.join(" "))
            }
          />
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <BasicInput
            formik={formik}
            fieldName={"amount"}
            saveError={saveError}
            label="Rate"
            type="number"
          />
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <DropDown
            mainClass="flex flex-row"
            dropDownContainer="w-[450px] z-[9998]"
            labelClassName="flex-grow"
            label="Currency"
            value={validCurrencies.find(
              (item) => item.value === formik.values.currency_code
            )}
            setValue={(selected) =>
              formik.setFieldValue("currency_code", selected.value)
            }
            options={validCurrencies}
            errorText={
              (formik.touched.currency_code && formik.errors.currency_code) ||
              (saveError?.status === 422 &&
                saveError?.response?.data?.errors?.currency_code?.join(" "))
            }
            disabled={isEdit}
          />
        </Col>
      </Row>
    </BasicModal>
  );
});

export default PlatformFeeModal;
